import { FieldDatePicker } from 'components/redesign/FieldDatePicker';
import { useForm } from 'react-hook-form';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { GetInWorkModalProps, WorkModalValues } from './types';
import { BaseError, OrderBecomeExecutorDataV3 } from 'types';
import { request } from 'api';
import { endpoints } from 'api/endpoints';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { ForbiddenErrorResponse } from '../types';
import { useAppSelector } from 'hooks/useAppSelector';
import { getProfileID } from 'reduxStore/reducers/ProfileSlice/selectors/getProfileID';
import { FieldInput } from 'components/redesign/FieldInput';
import { checkTime } from './consts';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { alertBaseError } from 'utils/helpers';
import { getTimezone } from 'reduxStore/reducers/ProfileSlice/selectors/getTimezone';

export const GetInWorkModal = ({ order, refetch }: GetInWorkModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    clearErrors,
    watch,
    setError,
  } = useForm<WorkModalValues>({});
  const userId = useAppSelector(getProfileID);
  const dispatch = useAppDispatch();
  const timeZone = useAppSelector(getTimezone);

  const becomeExecutorHandler = async (executorArrivingPlanDateTime: string) => {
    const orderId = order.id;
    const requestBody: OrderBecomeExecutorDataV3 = {
      userId,
      rowVersion: order.rowVersion,
      mainRequestId: orderId,
      executorArrivingPlanDateTime,
    };
    try {
      const response = await request(
        endpoints.BECOME_EXECUTOR_V3.type,
        endpoints.BECOME_EXECUTOR_V3.url(order.id),
        {
          data: requestBody,
        }
      );
      // Заново получаем данные после смены статуса
      if (response.status === 200) {
        toast.success('Заявка успешно взята в работу');
        refetch();
        dispatch(modalActions.closeModal());
      }
    } catch (error) {
      if ((error as AxiosError<ForbiddenErrorResponse>)?.response?.status === 403) {
        toast.error('Заявка уже в работе у другого Исполнителя');
      } else {
        alertBaseError(error as AxiosError<BaseError>);
      }
    }
  };

  const becomeExecutorByCancelledOrder = async (executorArrivingPlanDateTime: string) => {
    const data: OrderBecomeExecutorDataV3 = {
      userId,
      rowVersion: order.rowVersion,
      mainRequestId: order.id,
      executorArrivingPlanDateTime,
    };
    try {
      await request('post', `api/v3/Outsource/MainRequests/${order.id}/TakeFromFair`, {
        data,
      });
      toast.success('Заявка успешно взята в работу');
      refetch();
      dispatch(modalActions.closeModal());
    } catch (error) {
      if ((error as AxiosError<ForbiddenErrorResponse>)?.response?.status === 403) {
        toast.error('Заявка уже в работе у другого Исполнителя');
      } else {
        alertBaseError(error as AxiosError<BaseError>);
      }
    }
  };

  const submitHandler = () => {
    const toastErrors: string[] = [];
    const actualFormValues = getValues();

    checkTime(actualFormValues.time ?? [], setError, toastErrors);

    if (toastErrors.length) {
      toastErrors.forEach((message: string) => {
        toast.error(message);
      });
      return;
    }

    const resultDate = new Date(actualFormValues.date);

    const [hours, minutes] = actualFormValues.time.split(':').map(Number);

    // Создаем дату в UTC
    const utcDate = new Date(
      Date.UTC(
        resultDate.getFullYear(),
        resultDate.getMonth(),
        resultDate.getDate(),
        hours,
        minutes,
        0,
        0
      )
    );

    const utcDateWithOffset = new Date(utcDate.getTime() - timeZone * 60 * 60 * 1000);

    order?.actions[0] === 7 && becomeExecutorHandler(utcDateWithOffset.toISOString());
    order?.actions[0] === 29 && becomeExecutorByCancelledOrder(utcDateWithOffset.toISOString());
  };

  const isSubmitDisabled =
    !watch('date') ||
    !(
      watch('time') &&
      watch('time')
        .split(':')
        .every((item) => /^\d+$/.test(item))
    );

  return (
    <form className="mt-6 flex flex-col gap-6" onSubmit={handleSubmit(submitHandler)}>
      <FieldDatePicker
        name="date"
        control={control}
        label="Дата прибытия"
        clearErrors={clearErrors}
        minDate={new Date()}
      />
      <FieldInput
        name="time"
        control={control}
        label="Время прибытия"
        errorMessage={errors.time?.message}
        clearErrors={clearErrors}
        mask="99:99"
      />
      <div className="flex justify-end">
        <Button
          type="button"
          variant={ButtonVariant.GHOST}
          onClick={() => dispatch(modalActions.closeModal())}
        >
          ОТМЕНА
        </Button>
        <Button type="submit" disabled={isSubmitDisabled}>
          ВЗЯТЬ В РАБОТУ
        </Button>
      </div>
    </form>
  );
};
